import { EventEmitter, Output, ViewChild } from '@angular/core';
import { FormConfig } from '../../models/form-config';
import { FormFactoryService } from '../../services/form-factory.service';
import { AbstractControl, FormGroup } from '@angular/forms';
import { FieldConfig } from '../../models/field-config';
import { ControlChange } from '../../../core/models/control-change.model';
import { isNullOrUndefined } from 'util';
import { fillFormEvent } from '../../../core/utils/utils';
import { isNotNullOrUndefined } from 'codelyzer/util/isNotNullOrUndefined';
import { Memoize } from '../../utils/memoize';
import { StepKey } from '../../../core/enums/step-key.enum';
import { SectionComponent } from './section.component';
import {FieldType} from '../../models/field-type';
import {Subscription} from 'rxjs/Rx';

export abstract class SectionBase {

  key: string;
  title: string;
  bean: any = {};
  isDirtyBean: any = {};
  mobile: boolean;
  formConfig: FormConfig;
  formFactoryService: FormFactoryService;
  formGroup: FormGroup = new FormGroup({});
  sectionDataMock: any;
  fillFormEventSubscribed = false;
  sectionClass: string;

  @Output() sectionChange: EventEmitter<any> = new EventEmitter();
  @Output() sectionFieldChange: EventEmitter<void> = new EventEmitter();
  @ViewChild('parentSection') parentSection: SectionComponent;

  get valid(): boolean {
    if (this.formFactoryService) {
      let valid = this.isSectionValid();
      return valid;
    }
  }

  get formValues(): any {
    return this.formGroup.value;
  }

  buildForm(): void {
    this.formFactoryService = new FormFactoryService(this.formConfig, this.bean);
    this.formFactoryService.buildFormGroup(this.formGroup);
    this.handleFillFormEvent();
  }

  handleFillFormEvent(): void {
    if (!this.fillFormEventSubscribed) {
      fillFormEvent.subscribe(data => this.fillForm(data));
      this.fillFormEventSubscribed = true;
    }
  }

  submitSection(): void {
    if (isNotNullOrUndefined(this.formFactoryService)) {
      this.formFactoryService.submitForm();
    }
  };

  isSectionValid(): boolean {
    return this.formFactoryService.isFormValid();
  }

  validateSection(): void {
    this.formFactoryService.validateSection();
  }

  areErrors(): boolean {
    if (this.formFactoryService && this.formFactoryService.errorsMap) {
      return this.formFactoryService.errorsMap.size !== 0;
    }
    return false;
  }

  getFieldConfig(fieldKey: string): FieldConfig {
    return this.formFactoryService.fieldConfig(fieldKey);
  }

  getControl(key: string): AbstractControl {
    return this.formGroup.controls[key];
  }

  get previewMode(): boolean {
    return this.parentSection && this.parentSection.isPreviewMode;
  }

  canFieldDisplayed(fieldKey: string): boolean {
    return ((this.previewMode && this.getFieldConfig(fieldKey).displayInEditableMode) || !this.previewMode);
  }

  handleControlsState(...controls: ControlChange[]): void {
    if (controls.length === 0) {
      return;
    }

    controls.map(control => this.saveControlChange(control));
  }

  clearMultipleValidators(...controlNames: string[]): void {
    if (controlNames.length === 0) {
      return;
    }

    controlNames.map(name => {
      this.getControl(name).clearValidators();
    })
  }

  resetMultipleControls(...controlNames: string[]): void {
    if (controlNames.length === 0) {
      return;
    }

    controlNames.map(name => {
      this.getControl(name).reset();
    });
  }

  initControlsValidators(...fieldKeys: string[]): void {
    fieldKeys.forEach(key => {
      if (isNotNullOrUndefined(this.getFieldConfig(key))) {
        let validators = this.getFieldConfig(key).validators;
        if (validators) {
          let transformedValidatorsToAngularValidators = this.formFactoryService.formService.getValidators(validators);
          this.getControl(key).setValidators(transformedValidatorsToAngularValidators);
        }
      }
    })
  }

  updateValueAndValidity(...fieldKeys: string[]): void {
    fieldKeys.forEach(key => {
      if (isNotNullOrUndefined(this.getControl(key))) {
        this.getControl(key).updateValueAndValidity({emitEvent: false});
      }
    });
  }

  onFieldChange(event): void {
    this.sectionFieldChange.emit();
  }

  onStepSubmitted(stepValidity: boolean): void {

  }

  fillForm(data: any) {
    this.initDataMockForCurrentSection(data);

    if (isNotNullOrUndefined(this.sectionDataMock)) {
      let control, key, value;
      this.formConfig.fieldsConfig.forEach(fieldConfig => {
        key = fieldConfig.fieldKey;
        control = this.getControl(key);
        value = this.getMockValue(key);
        if (!isNullOrUndefined(value)) {
          control.setValue(value);
        }
      });
    }
  }

  getMockValue(fieldKey: string): any {
    let value;
    fieldKey += '';
    if (fieldKey.includes('.')) {
      let parts = fieldKey.split('.');
      value = this.sectionDataMock[parts[0]][parts[1]];
    } else {
      value = this.sectionDataMock[fieldKey];
    }
    return value;
  }

  isFieldTypeRadio(fieldKey: string): boolean {
    return this.getFieldConfig(fieldKey).type === FieldType.Radio;
  }

  isFieldTypeButtonToggle(fieldKey: string): boolean {
    return this.getFieldConfig(fieldKey).type === FieldType.ButtonToggle;
  }

  isFieldTypeRadioCheckboxTile(fieldKey: string): boolean {
    return this.getFieldConfig(fieldKey).type === FieldType.RadioCheckboxTile;
  }

  enableField(fieldKey: string): void {
    this.getControl(fieldKey).enable();
  }

  disableField(fieldKey: string): void {
    this.getControl(fieldKey).disable();
  }

  @Memoize()
  getContextString(key: string): string {
    return `${this.formConfig.formKey}.${key}`;
  }

  abstract initBean(): void;

  abstract createConfig(): void;

  abstract initControlsChanges(): void;

  private saveControlChange({ controlName, behaviour }: ControlChange): void {
    if (isNotNullOrUndefined(this.getControl(controlName))) {
      this.getControl(controlName).valueChanges.subscribe(value => behaviour(value));
    }
  }

  private initDataMockForCurrentSection(data: any): void {
    const { stepKey, mock } = data;
    const stepDataMock = this.isStepOcDetails(stepKey) ? mock['ocDetails'] : mock[stepKey];
    if (this.isStepOcDetails(stepKey)) { // first step
      switch (this.formConfig.formKey) {
        case 'INITIAL_DATA_SECTION':
          this.sectionDataMock = { ...stepDataMock.vehicle, ...stepDataMock.owner };
          break;
        case 'CALCULATION_AGREEMENTS_SECTION':
          this.sectionDataMock = stepDataMock.contact;
          break;
        case 'VEHICLE_MODEL_SECTION':
          this.sectionDataMock = stepDataMock.vehicle;
          break;
        case 'VEHICLE_USE_SECTION':
          this.sectionDataMock = stepDataMock.vehicle;
          break;
        case 'VEHICLE_DATA_SECTION':
          this.sectionDataMock = stepDataMock.vehicle;
          break;
        case 'OWNER_DATA_SECTION':
          this.sectionDataMock = { ...stepDataMock.owner, ...stepDataMock.contact };
          break;
        case 'CONTACT_SECTION':
          this.sectionDataMock = stepDataMock.contact;
          break;
        default:
          this.sectionDataMock = null;
      }
    } else if (stepKey === StepKey.THIRD_STEP) { // third step
      switch (this.formConfig.formKey) {
        case 'ADDITIONAL_INFORMATION_SECTION':
          this.sectionDataMock = stepDataMock.vehicle;
          break;
        case 'INSURER_DATA_SECTION':
          this.sectionDataMock = stepDataMock.insurer;
          break;
        case 'SUMMARY_SECTION':
          this.sectionDataMock = stepDataMock.agreements;
          break;
        case 'METHOD_PAYMENT_SECTION':
          this.sectionDataMock = stepDataMock.paymentMethod;
          break;
        default:
          this.sectionDataMock = null;
      }
    } else {
      this.sectionDataMock = null;
    }
  }

  private isStepOcDetails(stepKey: StepKey): boolean {
    return stepKey === StepKey.FIRST_STEP_0 || stepKey === StepKey.FIRST_STEP_1 || stepKey === StepKey.FIRST_STEP_2 || stepKey === StepKey.FIRST_STEP_3;
  }

}
